import React, { useState } from "react";
import { History, Trash2, UserX, Undo2 } from "lucide-react";
import AthleteBadges from "@/components/AthleteBadges";
import ConfrontoIndicator from "@/components/ConfrontoIndicator";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "");
const ACTION = { create: "lançou", edit: "alterou", delete: "removeu" };

const HistoryPopover = ({ entries, athleteId }) => {
  const [open, setOpen] = useState(false);
  if (!entries?.length) return null;
  return (
    <span className="relative inline-block ml-2 align-middle">
      <button
        type="button"
        data-testid={`sumula-history-${athleteId}`}
        className="inline-flex items-center text-[var(--text-secondary)] hover:text-[#C9A84C] whitespace-nowrap"
        title={`${entries.length} alteração(ões)`}
        onClick={() => setOpen((v) => !v)}
      >
        <History size={14} />
        <span className="text-[10px] ml-0.5 tabular-nums">{entries.length}</span>
      </button>
      {open && (
        <div
          data-testid={`sumula-history-pop-${athleteId}`}
          className="absolute z-30 left-0 top-6 w-80 card p-3 text-xs shadow-2xl border border-[#C9A84C]/30"
          onMouseLeave={() => setOpen(false)}
        >
          <div className="overline mb-2 text-[#C9A84C]">Histórico da nota</div>
          <ul className="space-y-1 max-h-48 overflow-y-auto">
            {entries.map((h) => (
              <li key={h.id} className="border-b border-white/5 pb-1">
                <span className="tabular-nums text-[var(--text-secondary)]">{fmt(h.at)}</span> · <b>{h.by_name}</b>{" "}
                {ACTION[h.action] || h.action}{" "}
                {h.action === "edit" && (
                  <span className="tabular-nums">
                    <span className="line-through opacity-60">{h.from_nota}</span> → <b className="text-[#C9A84C]">{h.to_nota}</b>
                  </span>
                )}
                {h.action === "create" && <b className="text-[#5AF78E] tabular-nums">{h.to_nota}</b>}
                {h.action === "delete" && <span className="line-through opacity-60 tabular-nums">{h.from_nota}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </span>
  );
};

export const ScoreRow = ({ a, cur, saved, history, confrontos, locked, isAdmin, judgeId, onChange, onRemove, onToggleWo }) => {
  const disabled = locked || a.wo || !judgeId;
  return (
    <tr data-testid={`sumula-row-${a.id}`} className={a.wo ? "opacity-60" : ""}>
      <td className="tabular-nums font-display font-bold text-lg">{a.numero}</td>
      <td>
        <span className={"align-middle " + (a.wo ? "line-through" : "")}>{a.nome}</span>
        {a.wo && (
          <span data-testid={`sumula-wo-badge-${a.id}`} className="ml-2 px-1.5 py-0.5 text-[9px] font-black tracking-widest bg-[#FF453A] text-white align-middle">
            W.O.
          </span>
        )}
        <AthleteBadges athlete={a} />
        <ConfrontoIndicator confrontos={confrontos} currentAthleteId={a.id} testId={`sumula-confronto-${a.id}`} />
        <HistoryPopover entries={history} athleteId={a.id} />
      </td>
      <td className="text-xs text-[var(--text-secondary)]">{a.equipe || "—"}</td>
      <td>
        <input
          data-testid={`sumula-nota-${a.id}`}
          type="number"
          step="1"
          min="1"
          max="30"
          className="input-field text-center font-display font-bold text-lg py-1"
          value={a.wo ? "" : cur.nota}
          onChange={(e) => onChange({ ...cur, nota: e.target.value })}
          disabled={disabled}
          placeholder={a.wo ? "W.O." : ""}
        />
      </td>
      <td>
        <input
          data-testid={`sumula-obs-${a.id}`}
          className="input-field py-1"
          value={cur.obs}
          onChange={(e) => onChange({ ...cur, obs: e.target.value })}
          placeholder="—"
          disabled={disabled}
        />
      </td>
      <td className="text-right pr-2 whitespace-nowrap">
        {saved && !locked && !a.wo && (
          <button
            type="button"
            data-testid={`sumula-remove-${a.id}`}
            className="btn-ghost !px-2 !py-1 !text-[#FF453A]"
            title="Remover nota deste atleta"
            onClick={() => onRemove(saved)}
          >
            <Trash2 size={14} />
          </button>
        )}
        {isAdmin && !locked && (
          <button
            type="button"
            data-testid={`sumula-wo-${a.id}`}
            className={"btn-ghost !px-2 !py-1 ml-1 " + (a.wo ? "!text-[#5AF78E]" : "!text-[#FFCC00]")}
            title={a.wo ? "Desfazer W.O. (atleta volta ao ranking)" : "Marcar W.O. — ausente do palco"}
            onClick={() => onToggleWo(a)}
          >
            {a.wo ? <Undo2 size={14} /> : <UserX size={14} />}
          </button>
        )}
      </td>
    </tr>
  );
};
